import { useState, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import categoryService from "../services/categoryService"
import storyEventService from "../services/storyEventService"
import timelineService from "../services/timelineService"
import AdminDialog from "./adminAyarlar"
import { generateFullTimelineIframeCode } from "../utils/embed"
import EmbedModal from "./EmbedModal"
import EventNavigationControls from "./EventNavigationControls"
import { useLocation } from "react-router-dom"
const Navbar = ({
	timelineId,
	onTimelineUpdate,
	isAuthenticated,
	selectedEvent,
	onNext,
	onPrevious,
	isAutoPlaying,
	onAutoPlayToggle,
	onGoToFirst,
	onGoToLast,
	isReading,
	onReadingStateUpdate,
}) => {
	const navigate = useNavigate()
	const { user, logout } = useAuth()
	const [showAdminDialog, setShowAdminDialog] = useState(false)
	const [showEmbedModal, setShowEmbedModal] = useState(false)
	const [embedOptions, setEmbedOptions] = useState({
		width: "100%",
		height: "650px",
		h: "700",
		ts: "100",
	})
	const [embedCode, setEmbedCode] = useState("")
	const [categories, setCategories] = useState([])
	const [storyEvents, setStoryEvents] = useState([])
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState(null)
	const [isLargeScreen, setIsLargeScreen] = useState(
		window.innerWidth >= 1024
	)
	// Get query parameters
	const location = useLocation()
	const queryParams = new URLSearchParams(location.search)
	const isembed = queryParams.get("h") || queryParams.get("ts")
	const [timelineData, setTimelineData] = useState({
		title: "",
		intro: "",
		about: "",
		startDate: {
			year: new Date().getFullYear(),
			month: new Date().getMonth(),
			day: new Date().getDate(),
			isBC: false,
		},
		endDate: {
			year: new Date().getFullYear(),
			month: new Date().getMonth(),
			day: new Date().getDate(),
			isBC: false,
		},
		backgroundColor: "#FFFFFF",
		backgroundImage: "",
		logoUrl: "/trbayrak.png",
		beginAt: "firstStory",
	})

	useEffect(() => {
		const handleResize = () => {
			setIsLargeScreen(window.innerWidth >= 1024)
		}

		window.addEventListener("resize", handleResize)
		return () => window.removeEventListener("resize", handleResize)
	}, [])

	// Fetch timeline data from backend - wrap in useCallback
	const fetchTimelineData = useCallback(async () => {
		try {
			const data = await timelineService.getTimelineById(timelineId)
			setTimelineData(data)
			setLoading(false)
		} catch (err) {
			console.error("Error fetching timeline data:", err)

			// Handle authentication errors, but don't redirect non-authenticated users
			if (err.response && err.response.status === 401 && user) {
				setError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.")
				setTimeout(() => {
					logout()
					navigate("/login")
				}, 2000)
			} else {
				setError("Zaman çizelgesi yüklenirken hata oluştu")
			}

			setLoading(false)
		}
	}, [timelineId, logout, navigate, user])

	// Fetch categories from the backend - wrap in useCallback
	const fetchCategories = useCallback(async () => {
		try {
			setLoading(true)
			const fetchedCategories = await categoryService.getCategories(
				timelineId
			)
			setCategories(fetchedCategories)
			setLoading(false)
		} catch (error) {
			console.error("Error fetching categories:", error)

			// Handle authentication errors
			if (error.response && error.response.status === 401) {
				setError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.")
			} else {
				setError("Kategoriler yüklenirken hata oluştu")
			}

			setLoading(false)
		}
	}, [timelineId])

	// Fetch story events from the backend - wrap in useCallback
	const fetchStoryEvents = useCallback(async () => {
		try {
			setLoading(true)
			const fetchedStoryEvents = await storyEventService.getStoryEvents(
				timelineId
			)
			setStoryEvents(fetchedStoryEvents)
			setLoading(false)
		} catch (error) {
			console.error("Error fetching story events:", error)

			// Handle authentication errors
			if (error.response && error.response.status === 401) {
				setError("Oturum süresi dolmuş. Lütfen tekrar giriş yapın.")
			} else {
				setError("Hikayeler yüklenirken hata oluştu")
			}

			setLoading(false)
		}
	}, [timelineId])

	// Fetch timeline data when component mounts
	useEffect(() => {
		if (timelineId) {
			fetchTimelineData()
		}

		// Only fetch these if user is authenticated
		if (timelineId && user) {
			fetchCategories()
			fetchStoryEvents()
		}
	}, [timelineId, user, fetchTimelineData, fetchCategories, fetchStoryEvents])

	// Handle logout
	const handleLogout = () => {
		logout()
		navigate("/login")
	}

	// Handle settings changes from AdminDialog
	const handleSettingsChange = async (newSettings) => {
		try {
			setLoading(true)

			// Ensure background settings are properly formatted
			if (!newSettings.backgroundColor) {
				newSettings.backgroundColor = "#FFFFFF"
			}

			await timelineService.updateTimeline(timelineId, newSettings)

			// First update local state
			setTimelineData(newSettings)

			// Then notify parent component (Timeline.jsx) to refresh data
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)

			// Provide user feedback
			setError(null)

			return true // Return success status
		} catch (err) {
			console.error("Error updating settings:", err)
			setError("Ayarlar güncellenirken hata oluştu")
			setLoading(false)
			return false // Return failure status
		}
	}

	// Handle adding a new story event
	const handleAddStory = async (storyData) => {
		try {
			setLoading(true)
			await storyEventService.createStoryEvent(timelineId, {
				...storyData,
				timeline_id: timelineId,
				category_id: storyData.category_id || null,
			})

			// Refresh stories locally
			await fetchStoryEvents()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
		} catch (error) {
			console.error("Error adding story:", error)
			setError("Hikaye eklenirken hata oluştu")
			setLoading(false)
		}
	}

	// Handle editing a story event
	const handleEditStory = async (storyData) => {
		try {
			setLoading(true)

			// Ensure the date is properly formatted with year, month, day
			const formattedStoryData = { ...storyData }

			// Check if the date property is already in the correct format
			const hasCorrectDateFormat =
				formattedStoryData.date &&
				typeof formattedStoryData.date.year === "number" &&
				typeof formattedStoryData.date.month === "number" &&
				typeof formattedStoryData.date.day === "number"

			// If date is not in the correct format but we have a date object
			if (!hasCorrectDateFormat && formattedStoryData.date) {
				// Try to extract date components if it's a CustomDate object
				if (typeof formattedStoryData.date.getFullYear === "function") {
					formattedStoryData.date = {
						year: formattedStoryData.date.getFullYear(),
						month: formattedStoryData.date.getMonth(),
						day: formattedStoryData.date.getDate(),
						isBC: formattedStoryData.date.isBC || false,
					}
				}
				// If it has a date string property
				else if (formattedStoryData.date.date) {
					const dateObj = new Date(formattedStoryData.date.date)
					formattedStoryData.date = {
						year: dateObj.getFullYear(),
						month: dateObj.getMonth(),
						day: dateObj.getDate(),
						isBC: formattedStoryData.date.isBC || false,
					}
				}
			}

			await storyEventService.updateStoryEvent(
				timelineId,
				formattedStoryData._id,
				formattedStoryData
			)

			// Refresh stories locally
			await fetchStoryEvents()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
		} catch (error) {
			console.error("Error updating story:", error)
			setError("Hikaye güncellenirken hata oluştu")
			setLoading(false)
		}
	}

	// Handle deleting a story event
	const handleDeleteStory = async (storyId) => {
		try {
			setLoading(true)
			await storyEventService.deleteStoryEvent(timelineId, storyId)

			// Refresh stories locally
			await fetchStoryEvents()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
		} catch (error) {
			console.error("Error deleting story:", error)
			setError("Hikaye silinirken hata oluştu")
			setLoading(false)
		}
	}

	// Pass category handlers to AdminDialog
	const handleAddCategory = async (categoryData) => {
		try {
			setLoading(true)
			const newCategory = await categoryService.createCategory(
				timelineId,
				{
					...categoryData,
					timeline_id: timelineId,
				}
			)

			// Refresh categories locally
			await fetchCategories()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
			return newCategory
		} catch (error) {
			console.error("Error adding category:", error)
			setError("Kategori eklenirken hata oluştu")
			setLoading(false)
			throw error
		}
	}

	const handleEditCategory = async (categoryData) => {
		try {
			setLoading(true)
			const updatedCategory = await categoryService.updateCategory(
				categoryData._id,
				categoryData
			)

			// Refresh categories locally
			await fetchCategories()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
			return updatedCategory
		} catch (error) {
			console.error("Error updating category:", error)
			setError("Kategori güncellenirken hata oluştu")
			setLoading(false)
			throw error
		}
	}

	const handleDeleteCategory = async (categoryId) => {
		try {
			setLoading(true)
			await categoryService.deleteCategory(categoryId)

			// Refresh categories locally
			await fetchCategories()

			// Notify parent component
			if (onTimelineUpdate) {
				onTimelineUpdate()
			}

			setLoading(false)
			setError(null)
		} catch (error) {
			console.error("Error deleting category:", error)
			setError("Kategori silinirken hata oluştu")
			setLoading(false)
			throw error
		}
	}

	// Handle timeline embed generation
	const handleTimelineEmbed = async () => {
		try {
			setShowEmbedModal(true)
			const code = generateFullTimelineIframeCode(
				timelineData,
				window.location.origin,
				embedOptions
			)
			setEmbedCode(code)
		} catch (error) {
			console.error("Embed kodu oluşturulurken hata:", error)
			setError("Embed kodu oluşturulurken hata oluştu")
		}
	}

	const handleEmbedOptionsChange = (newOptions) => {
		setEmbedOptions(newOptions)
		const code = generateFullTimelineIframeCode(
			timelineData,
			window.location.origin,
			newOptions
		)
		setEmbedCode(code)
	}

	return (
		<>
			{/* Logo area - Always visible */}
			<div className="fixed top-0 left-0 flex bg-gray-200 z-50 w-[200px] h-[112px] max-w-[200px] justify-between items-center shadow-md p-1">
				{/* Fixed Logo (left side) */}
				<a href="/" className="h-full w-[50%]">
					<img
						src="/seyfettinLogo.jpg"
						alt="Site Logo"
						className="h-full w-full object-contain"
					/>
				</a>

				{/* Changeable Logo (right side) */}
				<img
					src={timelineData.logoUrl || "/trbayrak.png"}
					alt="Zaman Çizelgesi Logo"
					className="h-full w-[50%] object-contain"
				/>
			</div>

			{/* Navbar - Starting from the right of the logo area */}
			<nav
				className="fixed justify-between top-0 right-0 bg-black text-white shadow-lg z-40 flex items-center h-16 px-4"
				style={{ left: "200px" }}
			>
				<div className="pl-4 text-nowrap text-2xl">
					{timelineData.title}
				</div>
				{isAuthenticated && user && timelineData && user._id === timelineData.user_id && !isembed && (
					<div className="flex items-center space-x-4 w-full justify-end">
						<button
							onClick={handleTimelineEmbed}
							className="bg-purple-600 hover:bg-purple-700 p-2 rounded transition-colors"
						>
							<svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5z" clipRule="evenodd" />
							</svg>
						</button>

						<button
							onClick={() => setShowAdminDialog(true)}
							className="bg-gray-800 hover:bg-gray-700 p-2 rounded transition-colors mr-8"
						>
							Ayarlar
						</button>
					</div>
				)}
				{isAuthenticated && !isembed && (
					<div className="flex items-center space-x-4 ml-auto">
						<button
							onClick={() => navigate("/dashboard")}
							className="bg-gray-800 hover:bg-gray-700 p-2 rounded transition-colors"
						>
							Gir
						</button>

						<button
							onClick={handleLogout}
							className="bg-red-600 hover:bg-red-700 p-2 rounded transition-colors"
						>
							Çıkış
						</button>
					</div>
				)}
				{!isLargeScreen && (
					<EventNavigationControls
						selectedEvent={selectedEvent}
						isReading={isReading}
						onPrevious={onPrevious}
						onGoToFirst={onGoToFirst}
						onGoToLast={onGoToLast}
						onNext={onNext}
						onAutoPlayToggle={onAutoPlayToggle}
						onReadingStateUpdate={onReadingStateUpdate}
						isAutoPlaying={isAutoPlaying}
					/>
				)}
			</nav>

			{/* Admin Dialog - Only show when user is authenticated AND owns the timeline */}
			{showAdminDialog && isAuthenticated && user && timelineData && user._id === timelineData.user_id && !isembed && (
				<AdminDialog
					open={showAdminDialog}
					onClose={() => setShowAdminDialog(false)}
					settings={timelineData}
					onSettingsChange={handleSettingsChange}
					onAddStory={handleAddStory}
					onEditStory={handleEditStory}
					onDeleteStory={handleDeleteStory}
					onAddCategory={handleAddCategory}
					onEditCategory={handleEditCategory}
					onDeleteCategory={handleDeleteCategory}
					categories={categories}
					storyEvents={storyEvents}
				/>
			)}

			{/* Embed Modal - Conditionally render based on state, trigger button handles ownership */}
			<EmbedModal
				isOpen={showEmbedModal}
				onClose={() => setShowEmbedModal(false)}
				embedCode={embedCode}
				options={embedOptions}
				onOptionsChange={handleEmbedOptionsChange}
			/>

			{/* Error message */}
			{error && (
				<div className="fixed bottom-4 right-4 bg-red-500 text-white px-4 py-2 rounded shadow-lg">
					{error}
				</div>
			)}
		</>
	)
}

export default Navbar
